import React, { useEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux'
import API from '../../../utils/API';
import MesTable from '../../../components/Table/MesTable';
import stopFilled from '../../../assets/icons/message-manager/stopFilled.svg';
import constants from '../../../utils/constants';
import commonMethods from '../../../utils/common-methods';
import { FormattedMessage } from 'react-intl';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';

const MESSENGER_TEXT_PREFIX = "MESSENGER_";

function MessageStatusTable(props) {
  const { updateFlag, setUpdateFlag, onLoadStatusTable } = props;
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('sent');
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusData, setStatusData] = useState([]);

  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const modalRef = useRef(null);

  useEffect(() => {
    modalRef.current = new window.bootstrap.Modal(document.getElementById('notificationModal'));
  }, []);

  const loadStatusTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      const res = await API.getqueuedMessages();
      res.data.forEach((mess) => {
        map.data[mess.id] = {
          id: mess.id,
          message: mess.title,
          type: mess.format,
          sent: mess.scheduler.timeStart ? commonMethods.formatDate(mess.scheduler.timeStart) : null,
          groups: mess.groups.map((role) => role.name).join(', '),
          status: mess.status,
          checked: false,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setStatusData(res.data);
      setDataCount(map.ids.length);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);
    } catch (err) {
      // If it's error 502, skip error handling
      if (err.response && err.response.status === 502)
        return; 
      setError(err.message);
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    if (onLoadStatusTable) {
      onLoadStatusTable(() => loadStatusTable(dataMap));
    }
  }, [onLoadStatusTable]);

  const dataMap = useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
    loadStatusTable(map);
    return map;
  }, []);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, statusData]);

  useEffect(() => {
    loadStatusTable(dataMap);
  }, [updateFlag]);

  const handleSingleDeletess = async (e, id) => {
    e.preventDefault();
    try {
      await API.stopMessages(id);
      setSuccess('Messages deleted successfully');
      modalRef.current.hide();
      setUpdateFlag(prev => !prev);
    } catch (error) {
      setError('Error deleting messages');
      modalRef.current.hide();
    }
  };

  const tableColumnArray = [
    {
      Header: 'MESSAGE',
      id: 'message',
      accessor: 'message',
      isVisible: true,
    },
    {
      Header: 'TYPE',
      id: 'type',
      accessor: 'type',
      isVisible: true,
    },
    {
      Header: 'SENT',
      id: 'sent',
      accessor: 'sent',
      isVisible: true,
    },
    {
      Header: 'GROUPS',
      id: 'groups',
      isVisible: true,
      accessor: 'groups',
    },
    {
      Header: 'STATUS',
      id: 'status',
      isVisible: true,
      Cell: ({ value }) => (
        <div><FormattedMessage id={MESSENGER_TEXT_PREFIX+value}/></div>
      ),
      accessor: 'status',
    },
    {
      Header: '',
      id: 'actions',
      width: 60,
      isVisible: true,
      Cell: ({ row }) => (
        <div className="actions">
          <button
            className="btn"
            id="stopMessage"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              handleSingleDeletess(e, row.original.id);
            }}
          >
            <img src={stopFilled} className="deleteAction" alt={<FormattedMessage id="DELETE" />} />
          </button>
        </div>
      ),
    },
  ];

  const tableProps = {
    pageType: constants.pageType.MessageStatus,
    modalId: "Status",
    columns: tableColumnArray,
    data, setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success, setSuccess,
    error, setError,
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    initialContainerHeight: '288px',
  };

  return (
    <div>
      <MesTable {...tableProps} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(MessageStatusTable);
